// Redux Utils
import { useSelector, useDispatch } from "react-redux";
import {
  crearEmpresa,
  activarEmpresa,
  getEmpresaInfo,
} from "@/features/empresa/empresaThunks";

// React Utils
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { use, useEffect, useState, useMemo, useCallback } from "react";
// Contexto
import { useModalsContext } from "@/contexts/ModalsContext";
// Components
import SidebarCrear from "@/components/SidebarCrear";
import Footer from "@/components/Footer";
import { EmpresaFormProvider } from "@/hooks/useEmpresaForm";
import Modal from "@/components/Modals/Modal";

// Paquetes
import { Country, State, City } from 'country-state-city';

// Assets
import RUTExample from "@/assets/files/RUT_Example.pdf";
import DorsoCedulaExample from "@/assets/files/cedula_reverso_example.webp";
import FrenteCedulaExample from "@/assets/files/cedula_anverso_example.webp";
import CuentaBancaria from "@/assets/files/imagen_cuenta_bancaria_example.webp";
import EscrituraConstitucionExample from "@/assets/files/EscrituraPublica_PersonaJuridica_Example.pdf";

export default function CrearEmpresaLayout({ children }) {
  // Redux state for authentication
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const { infoEmpresa } = useSelector((state) => state.empresa);
  const { errorActivarEmpresa } = useSelector((state) => state.activarEmpresa);
  const dispatch = useDispatch();
  const [initialData, setInitialData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [empresaType, setEmpresaType] = useState(null);
  
  // Estados para manejar las opciones de ubicación
  const [availableCountries, setAvailableCountries] = useState([]);
  const [availableStates, setAvailableStates] = useState([]);
  const [availableCities, setAvailableCities] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);

  const params = useParams();

  const { modals, openModal, closeModal } = useModalsContext();

  // Agregar manejo de modal
  // Obtener la ruta actual
  const location = useLocation(); // Obtiene la URL actual
  const path = location.pathname;
  const navigate = useNavigate();

  // Cargar países disponibles al montar el componente
  useEffect(() => {
    const allCountries = Country.getAllCountries();
    console.log("All Countries:", allCountries);
    
    // Por ahora solo permitir Chile
    // Para agregar más países, agrega sus códigos ISO al array: ['CL', 'AR', 'PE', 'CO', 'MX', 'BR']
    const allowedCountryCodes = ['CL'];
    const countries = allCountries.filter(country => allowedCountryCodes.includes(country.isoCode));
    
    console.log("Filtered Countries:", countries);
    setAvailableCountries(countries);
  }, []);

  // Callback para manejar cambios en el tipo de empresa y ubicación
  const handleEmpresaTypeChange = useCallback((fieldName, value) => {
    if (fieldName === 'empresa_type') {
      setEmpresaType(value);
    }
    
    // Manejar cambio de país
    if (fieldName === 'pais') {
      const country = availableCountries.find(c => c.isoCode === value);
      setSelectedCountry(country);
      
      if (country) {
        const states = State.getStatesOfCountry(country.isoCode);
        setAvailableStates(states);
      } else {
        setAvailableStates([]);
      }
      setAvailableCities([]);
      setSelectedState(null);
    }
    
    // Manejar cambio de región/estado
    if (fieldName === 'region') {
      const state = availableStates.find(s => s.isoCode === value);
      setSelectedState(state);
      
      if (state && selectedCountry) {
        const cities = City.getCitiesOfState(selectedCountry.isoCode, state.isoCode);
        setAvailableCities(cities);
      } else {
        setAvailableCities([]);
      }
    }

    if (fieldName === 'ciudad') {
      const city = availableCities.find(c => c.name === value);
      setSelectedCity(city);
    }
  }, [availableCountries, availableStates, selectedCountry]);

  // Cargar datos de la empresa si estamos en la ruta de activar
  useEffect(() => {
    if (path === `/empresa/${params.id}/activar` && user?.uid && !initialData) {
      const loadEmpresaData = async () => {
        try {
          setIsLoading(true);
          const empresaData = await dispatch(
            getEmpresaInfo({ uid: user.uid })
          ).unwrap();
          if (empresaData) {
            setInitialData(empresaData);
            if (empresaData.empresa_type) {
              setEmpresaType(empresaData.empresa_type);
            }
          }
        } catch (error) {
          console.log(
            "No se encontró información de empresa existente:",
            error
          );
          // Si no hay empresa, no es un error - puede ser la primera vez
        } finally {
          setIsLoading(false);
        }
      };

      loadEmpresaData();
    }
  }, [path, user?.uid, dispatch, initialData]);

  // Usar useMemo para recalcular steps cuando cambie empresaType
  const steps = useMemo(() => {
    let stepsArray = [];
    
    if (path == "/empresa/crear") {
    stepsArray = [
      {
        title: "Crear una Empresa",
        description:
          "Tu empresa es el lugar donde puedes convertirte y ser tu propia ticketera.",
        fields: [
          {
            name: "nombre",
            label: "Nombre de la empresa",
            type: "text",
            required: true,
          },
          {
            name: "categoria",
            label: "Categoría",
            type: "select",
            options: [
              "Artista",
              "Arte y Cultura",
              "Bienestar",
              "Deporte",
              "Gastronomía",
              "Ocio y Entretenimiento",
              "Emprendimiento",
              "Hospedaje",
              "Turismo",
              "Bares y Vida Nocturna",
              "Otros",
            ],
            required: true,
          },
          {
            name: "descripcion",
            label: "¿Qué te describe y qué es lo que haces?",
            type: "textarea",
            required: false,
          },
        ],
      },
      {
        title: "Ubicación",
        description:
          "Agrega la ubicación donde realizas tu actividad principal para que los usuarios puedan encontrarte fácilmente. Indica dónde operas habitualmente para que tus clientes puedan ubicarte. ¡Entre más preciso, mejor!",
        fields: [
          {
            name: "siubicacionfisica",
            label: "¿Tienes Ubicación Física?",
            type: "radio",
            options: ["Si", "No"],
            required: true,
          },
          {
            name: "pais",
            label: "País",
            type: "select",
            options: availableCountries.map(country => ({
              value: country.isoCode,
              label: `${country.flag} ${country.name}`
            })),
            required: true,
          },
          {
            name: "region",
            label: "Región / Estado",
            type: "select",
            options: availableStates.map(state => ({
              value: state.isoCode,
              label: state.name
            })),
            required: true,
            disabled: !selectedCountry,
          },
          {
            name: "ciudad",
            label: "Comuna / Ciudad",
            type: "select",
            options: availableCities.map(city => ({
              value: city.name,
              label: city.name
            })),
            required: false,
            disabled: !selectedState,
          },
          {
            name: "direccion",
            label: "Dirección",
            type: "text",
            required: false,
          },
          {
            name: "codigopostal",
            label: "Código Postal",
            type: "text",
            required: true,
          },
        ],
      },
      {
        // TODO: Agregar luego una forma para definir los horarios concrestos
        title: "Horarios",
        description:
          "Puedes definir la opción de los horarios, luego puedes editar y ajustar los horarios específicos en tu perfil de empresa.",
        fields: [
          {
            name: "horario",
            label: "Horarios",
            type: "radio",
            options: ["No disponible", "Siempre abierto", "Horario concreto"],
            required: true,
          },
        ],
      },
      {
        title: "Personaliza algunos detalles",
        description:
          "Agrega un imagen de portada y el logo de tu establecimiento para que los usuarios puedan reconocerte",
        fields: [
          {
            name: "portada",
            label: "Imagen de portada",
            type: "file",
            icon: "fa-solid fa-image",
            acceptedTypes: ["image/jpeg", "image/webp"],
            maxSize: 1, // MB
            required: true,
            helpText: "Sube una imagen (máx. 1MB)",
            showImage: "w-full h-full",
            showStyle: "",
          },
          {
            name: "imagenperfil",
            label: "Foto de Perfil",
            type: "file",
            icon: "fa-solid fa-camera",
            acceptedTypes: ["image/jpeg", "image/png", "image/webp"],
            maxSize: 1, // MB
            required: true,
            helpText: "Sube una imagen (máx. 1MB)",
            showImage: "w-40 h-40 mt-1 rounded-full mx-auto",
            showStyle: "w-40 h-40 mx-auto rounded-full",
          },
        ],
      },
    ];
  }
  if (path == `/empresa/${params.id}/activar`) {
    stepsArray = [
      // Step 1: Documentos Legales
      // RUT de la empresa (Chile).
      // Certificado de inicio de actividades del SII.
      {
        title: "Identidad de la empresa",
        description:
          "Estos documentos son necesarios para validar tu empresa y poder operar la facturación con nosotros.",
        fields: [
          {
            name: "empresa_type",
            label: "Qué tipo de empresa tienes?",
            tips: "No introduzcas puntos ni guiones.",
            type: "radio",
            options: ["Persona Natural", "Persona Jurídica"],
            required: true,
          },
        ],
      },
      // Step 3: Documentos bancarios
      // Poder notarial (si quien firma no es el representante legal).
      // Cuenta bancaria a nombre de la empresa (para pagos).
      {
        title: "Documentos importantes",
        description:
          "Estos documentos son necesarios para comprobar la legalidad de tu empresa y para gestionar los pagos de manera segura.",
        fields: [
          {
            name: "cuenta_bancaria",
            label: "Cuenta bancaria en que se recibirán los pagos",
            example: CuentaBancaria,
            type: "file",
            icon: "fa-solid fa-file-pdf",
            acceptedTypes: ["application/pdf", "image/jpeg", "image/png", "image/webp"],
            maxSize: 1, // MB
            required: true,
            helpText: "Sube un archivo (máx. 1MB)",
            showImage: "w-full h-full",
            showStyle: "",
          },
        ],
      },
      // Step 2: Documentos del representante legal
      // Patente comercial municipal (para locales físicos).
      // Cédula de identidad del representante legal.
      {
        title: "Identidad del propietario o representante legal",
        description:
          "En este paso, debes subir los documentos que acrediten tu identidad y en el caso de que seas persona jurídica, la identidad de representante legal de la empresa.",
        fields: [
          {
            name: "Frente_cedula_identidad",
            label: "Frente cédula de identidad",
            example: FrenteCedulaExample,
            type: "file",
            icon: "fa-solid fa-file-pdf",
            acceptedTypes: ["application/pdf", "image/jpeg", "image/png", "image/webp"],
            maxSize: 3, // MB
            required: true,
            helpText: "Sube un archivo (máx. 3MB)",
            showImage: "w-full h-full",
            showStyle: "",
          },
          {
            name: "Dorso_cedula_identidad",
            label: "Dorso cédula de identidad",
            example: DorsoCedulaExample,
            type: "file",
            icon: "fa-solid fa-file-pdf",
            acceptedTypes: ["application/pdf", "image/jpeg", "image/png", "image/webp"],
            maxSize: 3, // MB
            required: true,
            helpText: "Sube un archivo (máx. 3MB)",
            showImage: "w-full h-full",
            showStyle: "",
          },
        ],
      },
    ];

    switch (empresaType) {
      case "Persona Natural":
        // Agregar al primer paso los el campo de escribir el RUT en texto al momento de seleccionar el tipo de empresa
        stepsArray[0].fields.push({
          name: "rut_text",
          label: "RUT de la empresa",
          tips: "Formato: 12.345.678-9",
          type: "text",
          required: true,
        });
        break;
      case "Persona Jurídica":
        stepsArray[0].fields.push({
          name: "rut_text",
          label: "RUT de la empresa",
          tips: "Formato: 12.345.678-9",
          type: "text",
          required: true,
        });
        // Agregar al primer paso el campo de subir el RUT en archivo al momento de seleccionar el tipo de empresa que es tipo persona jurídica
        stepsArray[0].fields.push({
          name: "rut_file",
          label: "Rol Único Tributario",
          example: RUTExample,
          type: "file",
          icon: "fa-solid fa-file-pdf",
          acceptedTypes: ["application/pdf"],
          maxSize: 1, // MB
          required: true,
          helpText: "Sube un archivo (máx. 1MB)",
          showImage: "w-full h-full",
          showStyle: "",
          infoUrl: "https://www4.sii.cl/crepInternet/ConsultaCedulas.html",
          infoUrlLabel: "Consulta o descarga haciendo click en este link, el documento que estamos solicitando (SII)",
        });
        // Agregar el paso 3 el campo para poder ver la escritura de la constitución de la sociedad
        stepsArray[2].fields.push({
          name: "escritura_sociedad",
          label: "Escritura de constitución de la sociedad",
          example: EscrituraConstitucionExample,
          type: "file",
          icon: "fa-solid fa-file-pdf",
          acceptedTypes: ["application/pdf"],
          maxSize: 3, // MB
          required: true,
          helpText: "Sube un archivo (máx. 3MB)",
          showImage: "w-full h-full",
          showStyle: "",
        });
        break;
    }
  }

  return stepsArray;
  }, [path, empresaType, params.id, availableCountries, availableStates, availableCities, selectedCountry, selectedState]);

  // Definir actionFinishForm fuera de useMemo
  let actionFinishForm = null;
  if (path == "/empresa/crear") {
    actionFinishForm = async (data) => {
      try {
        setIsLoading(true);
        console.log("Formulario completado con datos:", data);

        // Preparar datos para envío al backend
        const empresaData = {
          nombre: data.nombre,
          categoria: data.categoria,
          descripcion: data.descripcion,
          siubicacionfisica: data.siubicacionfisica === "Si" ? 1 : 0,
          direccion: data.direccion,
          pais: selectedCountry?.name || data.pais,
          ciudad: selectedState?.name || data.region,
          comuna: selectedCity?.name || data.ciudad,
          codigopostal: data.codigopostal,
          horario: data.horario,
          portada: data.portada,
          imagenperfil: data.imagenperfil,
          idUsuario: user.uid,
        };

        // Enviar datos al backend
        await dispatch(crearEmpresa(empresaData)).unwrap();

        // Abrir modal de éxito
        openModal("crearEmpresa");
        setTimeout(() => {
          closeModal("crearEmpresa");
          navigate(`/perfil/${user.uid}`);
        }, 10000);
      } catch (error) {
        console.error("Error al crear empresa:", error);
        alert("Error al crear la empresa. Por favor, intenta de nuevo.");
      } finally {
        setIsLoading(false);
      }
    };
  }
  if (path == `/empresa/${params.id}/activar`) {
    actionFinishForm = async (data) => {
      try {
        setIsLoading(true);
        console.log("Formulario de activación completado con datos:", data);

        // Preparar datos para activación
        const activacionData = {
          uidEmpresa: params.id,
          empresa_type: data.empresa_type,
          rut_text: data.rut_text,
          Frente_cedula_identidad: data.Frente_cedula_identidad,
          Dorso_cedula_identidad: data.Dorso_cedula_identidad,
          cuenta_bancaria: data.cuenta_bancaria,
        };

        // Agregar campos opcionales para Persona Jurídica
        if (data.empresa_type === "Persona Jurídica") {
          if (data.rut_file) {
            activacionData.rut_file = data.rut_file;
          }
          if (data.escritura_sociedad) {
            activacionData.escritura_sociedad = data.escritura_sociedad;
          }
        }

        // Enviar datos al backend
        const result = await dispatch(activarEmpresa(activacionData)).unwrap();

        // Abrir modal de éxito
        openModal("activarEmpresa");
        setTimeout(() => {
          closeModal("activarEmpresa");
          navigate(`/empresa/${params.id}`);
        }, 5000);
      } catch (error) {
        console.error("Error al activar empresa:", error);
        // Abrir modal de error
        openModal("errorActivarEmpresa");
      } finally {
        setIsLoading(false);
      }
    };
  }
  const heightWindow = typeof window !== "undefined" ? window.innerHeight : 800;
  console.log("Height Window:", heightWindow);

  return (
    <>
      <EmpresaFormProvider>
        <div id="crear-layout">
          <main className="relative flex flex-row items-center justify-center h-dvh">
            <div
              id="main-content"
              className={`hidden lg:block relative flex-1 h-full overflow-hidden bg-aw-bgbluedark-30`}
            >
              <div className="relative">
                <div
                  className={`absolute bg-aw-bluedark-50 w-full h-full z-50`}
                ></div>
                <div
                  className={`relative flex flex-col items-center justify-center min-h-dvh pt-5 duration-300 transition-all`}
                >
                  {children}
                </div>
                <Footer />
              </div>
            </div>
            {user?.tipoDocumento === 'pasaporte' && (
              <div className="bg-amber-500/15 border border-amber-500/40 rounded-xl p-4 mb-4 flex items-start gap-3">
                <FontAwesomeIcon icon="fa-solid fa-triangle-exclamation" className="text-amber-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-amber-300 text-sm font-semibold mb-1">RUT chileno requerido</p>
                  <p className="text-amber-200/80 text-xs leading-relaxed">
                    Para activar tu empresa en AppWhoop se requiere un RUT chileno vigente (de persona natural o empresa). De momento, la activación solo está disponible para titulares con RUT chileno.
                  </p>
                </div>
              </div>
            )}
            <SidebarCrear
              steps={steps}
              onComplete={actionFinishForm}
              initialData={initialData}
              isLoading={isLoading}
              onFieldChange={handleEmpresaTypeChange}
            />
          </main>
        </div>
      </EmpresaFormProvider>
      {modals.crearEmpresa && (
        <Modal
          isOpen={modals.crearEmpresa}
          onClose={() => closeModal("crearEmpresa")}
          large="max-w-lg"
        >
          <div className="p-6 text-center bg-aw-bgbluedark rounded-lg">
            <FontAwesomeIcon
              icon="fa-solid fa-spinner"
              className="text-aw-green mb-4 animate-spin"
              size="3x"
            />
            <h3 className="text-lg text-aw-green font-semibold">
              ¡Empresa creada exitosamente!
            </h3>
            <p className="mt-2 text-gray-200">
              El siguiente paso para publicar tu empresa es ACTIVARLA.
            </p>
            <p className="mt-2 text-gray-200">
              Accede a tu nueva empresa desde tu perfil y desde las opciones
              podrás activar tu cuenta.
            </p>
            <p className="mt-2 text-gray-200 animate-pulse">Redirigiendo...</p>
          </div>
        </Modal>
      )}
      {modals.activarEmpresa && (
        <Modal
          isOpen={modals.activarEmpresa}
          onClose={() => closeModal("activarEmpresa")}
          large="max-w-lg"
        >
          <div className="p-6 text-center bg-aw-bgbluedark rounded-lg">
            <FontAwesomeIcon
              icon="fa-solid fa-circle-check"
              className="text-yellow-500 mb-4"
              size="3x"
            />
            <h3 className="text-lg text-yellow-500 font-semibold">
              ¡Solicitud Enviada!
            </h3>
            <p className="mt-2 text-gray-200">
              El proceso de activación está en revisión, pronto los usuarios verán tu empresa y tus tickets(eventos)
            </p>
            <p className="mt-2 text-gray-200 animate-pulse">Redirigiendo a tu empresa...</p>
          </div>
        </Modal>
      )}
      {modals.errorActivarEmpresa && (
        <Modal
          isOpen={modals.errorActivarEmpresa}
          onClose={() => closeModal("errorActivarEmpresa")}
          large="max-w-lg"
        >
          <div className="p-6 text-center bg-aw-bgbluedark rounded-lg">
            <FontAwesomeIcon
              icon="fa-solid fa-circle-xmark"
              className="text-red-500 mb-4"
              size="3x"
            />
            <h3 className="text-lg text-red-500 font-semibold">
              Error al activar la empresa
            </h3>
            <p className="mt-2 text-gray-200">
              {errorActivarEmpresa ||
                "Hubo un error al procesar tu solicitud. Por favor, verifica que todos los archivos estén correctamente cargados e intenta nuevamente."}
            </p>
            <button
              onClick={() => closeModal("errorActivarEmpresa")}
              className="mt-4 px-6 py-2 bg-aw-green text-aw-bluedark rounded-lg font-semibold hover:scale-95 transition-all"
            >
              Cerrar
            </button>
          </div>
        </Modal>
      )}
    </>
  );
}
